import { Component, computed, inject, input, output, viewChild } from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormControl, FormControlDirective, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';

import { BarI18nPipe } from '../i18n/bar-i18n.pipe';
import { BarListItem } from '../shared/bar-list-item.type';
import { BaasDropdownTooltip } from './baas-dropdown-tooltip';

@Component({
    selector: 'baas-select-list',
    templateUrl: './baas-select-list.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: BaasSelectList,
            multi: true
        }
    ],
    imports: [FormsModule, ReactiveFormsModule, NgSelectModule, BarI18nPipe, BaasDropdownTooltip]
})
/** Default simple select list component using <ng-select> working with BarListItem. */
export class BaasSelectList implements ControlValueAccessor {
    readonly #controlContainer = inject(ControlContainer, { optional: true });

    readonly ngSelect = viewChild(NgSelectComponent);
    readonly formControlDirective = viewChild(FormControlDirective);

    readonly items = input.required<BarListItem[]>();
    readonly _formControl = input<FormControl>(undefined, { alias: 'formControl' });
    readonly formControlName = input<string>();
    readonly multiple = input<boolean>();
    readonly checkboxMultiSelection = input(false);
    readonly maxSelectedItems = input<number>();
    readonly labelForId = input<string>();
    readonly clearable = input(true);
    readonly grouping = input<boolean>();
    readonly selectableGroup = input<boolean>(true);
    readonly closeOnSelect = input(true);
    readonly searchable = input(true);
    readonly readonly = input(false);
    readonly virtualScroll = input(false);
    /** Whether to skip focusing the clear action when navigating with Tab. */
    readonly skipClearActionTabFocus = input(false);

    readonly change = output<any>();
    readonly search = output<{
        term: string;
        items: any[];
    }>();

    protected readonly formControl = computed(() => {
        if (this._formControl()) return this._formControl();
        if (this.#controlContainer && this.formControlName()) {
            return <FormControl>this.#controlContainer.control.get(this.formControlName());
        }
        return undefined;
    });

    registerOnTouched(fn: any): void {
        this.formControlDirective().valueAccessor.registerOnTouched(fn);
    }

    registerOnChange(fn: any): void {
        this.formControlDirective().valueAccessor.registerOnChange(fn);
    }

    writeValue(obj: any): void {
        this.formControlDirective().valueAccessor.writeValue(obj);
    }

    setDisabledState(isDisabled: boolean): void {
        this.formControlDirective().valueAccessor.setDisabledState(isDisabled);
    }

    focus(): void {
        // timeout needed to have effect in certain situations (ngAfterViewInit)
        window.setTimeout(() => {
            this.ngSelect().searchInput().nativeElement.focus();
        });
    }
}
